import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Zap, CheckCircle2, XCircle, Clock, MessageCircleOff, Copy, Phone, Mail, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import PriorityBadge from "./PriorityBadge";
import ResponseBadge from "./ResponseBadge";
import WindowGauge from "./WindowGauge";
import AuditableFigure from "../common/AuditableFigure";
import EmptyState from "../common/EmptyState";
import { app } from "../../application";
import { fmtRelative } from "../../lib/format";
import { explainPriority } from "../../lib/priority";
import { OUTCOME_LABELS, timeSectionLabel } from "../../lib/copy";
import { T } from "../../lib/testids";

// Momento de confirmación de "primera acción" (una sola vez por navegador,
// nunca más) — refuerza que Commercial Operator ya está funcionando para
// Abel, no que acaba de rellenar un formulario. Local a propósito: no
// necesita backend ni cuenta de usuario, mismo patrón que el checklist de
// entrega (localStorage).
const FIRST_ACTION_KEY = "co_first_action_done_v1";

const OUTCOME_BUTTONS = [
  { key: "won", label: OUTCOME_LABELS.won, icon: CheckCircle2, color: "emerald" },
  { key: "in_progress", label: OUTCOME_LABELS.in_progress, icon: Clock, color: "amber" },
  { key: "lost", label: OUTCOME_LABELS.lost, icon: XCircle, color: "red" },
  { key: "no_response", label: OUTCOME_LABELS.no_response, icon: MessageCircleOff, color: "zinc" },
];

const OUTCOME_TONES = {
  emerald: {
    active: "border-emerald-500 bg-emerald-500/10 text-emerald-400",
    idle: "border-white/[0.08] text-zinc-400 hover:border-emerald-500/50 hover:text-emerald-400",
  },
  amber: {
    active: "border-amber-500 bg-amber-500/10 text-amber-400",
    idle: "border-white/[0.08] text-zinc-400 hover:border-amber-500/50 hover:text-amber-400",
  },
  red: {
    active: "border-red-500 bg-red-500/10 text-red-400",
    idle: "border-white/[0.08] text-zinc-400 hover:border-red-500/50 hover:text-red-400",
  },
  zinc: {
    active: "border-zinc-400 bg-zinc-500/10 text-zinc-200",
    idle: "border-white/[0.08] text-zinc-400 hover:border-zinc-500 hover:text-zinc-200",
  },
};

const CHANNEL_ICON = { call: Phone, email: Mail, whatsapp: Send };

// Siguiente acción comercial (P0 Sprint 11 noche): el webhook de respuestas
// entrantes (backend/app/routers/webhooks.py) antepone "[WhatsApp] <Clasificación>:"
// a las notas cuando clasifica una respuesta real. Esto solo LEE esa etiqueta
// para sugerir un paso humano concreto — nunca agenda ni llama nada solo.
function getNextAction(opp) {
  const match = (opp.notes || "").match(/^\[WhatsApp\] ([^:]+):/);
  const label = match?.[1];
  switch (label) {
    case "Interesado": return "Agendar demostración";
    case "Solicita llamada": return "Llamar al cliente";
    case "Solicita información": return "Enviar la información solicitada";
    case "Ya renovó": return "Confirmar la renovación y cerrar la oportunidad";
    case "Respondió":
    case "No clasificado": return "Revisar la respuesta y decidir el siguiente paso";
    default: return null;
  }
}

/**
 * OpportunityDetail — Unified operator panel used by Recovery, Turn mode, etc.
 * Purely composes provider calls; all data mutations flow through props.
 */
export default function OpportunityDetail({ opportunityId, onUpdated }) {
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggested, setSuggested] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [showFirstActionMoment, setShowFirstActionMoment] = useState(false);

  function celebrateIfFirstAction() {
    if (!localStorage.getItem(FIRST_ACTION_KEY)) {
      localStorage.setItem(FIRST_ACTION_KEY, "true");
      setShowFirstActionMoment(true);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!opportunityId) { setBundle(null); setLoading(false); return; }
      setLoading(true);
      const b = await app.opportunity.get(opportunityId);
      if (cancelled) return;
      setBundle(b);
      setSuggested(b?.play?.suggestedBody || "");
      setNotes(b?.opportunity?.notes || "");
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [opportunityId]);

  if (!opportunityId) {
    return (
      <EmptyState
        icon={Zap}
        title="Elige un cliente"
        description="Selecciona un cliente para ver por qué está aquí, qué le podrías decir, y registrar qué pasó."
      />
    );
  }
  if (loading || !bundle) {
    return <EmptyState title="Cargando..." />;
  }

  const { opportunity: opp, play } = bundle;

  const setOutcome = async (result) => {
    setSaving(true);
    try {
      const updated = await app.opportunity.recordOutcome(opp.id, { result, notes });
      const label = OUTCOME_BUTTONS.find((o) => o.key === result)?.label || result;
      toast.success(`Registrado: ${label}`);
      onUpdated?.(updated);
      setBundle((prev) => ({ ...prev, opportunity: updated }));
      celebrateIfFirstAction();
    } catch (e) {
      toast.error("No se pudo registrar el resultado");
    }
    setSaving(false);
  };

  const RESPONSE_CONFIRMATION = {
    replied: "Registrado — la vas a ver priorizada en Hoy hasta que la cierres.",
    interested: "¡Buena señal! La vas a ver como tu próxima conversación en Hoy.",
    not_interested: "Registrado. Ya no va a competir por tu atención hoy.",
  };

  const setResponseStatus = async (status) => {
    setSaving(true);
    try {
      const updated = await app.opportunity.updateResponseStatus(opp.id, status);
      toast.success(RESPONSE_CONFIRMATION[status] || "Actualizado");
      onUpdated?.(updated);
      setBundle((prev) => ({ ...prev, opportunity: updated }));
      celebrateIfFirstAction();
    } catch (e) {
      toast.error("No se pudo actualizar");
    }
    setSaving(false);
  };

  const savePlay = async () => {
    setSaving(true);
    try {
      const updated = await app.opportunity.updatePlayBody(opp.id, suggested);
      toast.success("Play guardado");
      setBundle((prev) => ({ ...prev, play: updated }));
    } catch (e) {
      toast.error("No se pudo guardar");
    }
    setSaving(false);
  };

  const ChannelIcon = CHANNEL_ICON[play?.channel] || Send;
  const nextAction = opp.responseStatus !== "not_interested" ? getNextAction(opp) : null;
  // Mismo criterio que "Tu siguiente conversación" en Hoy (lib/priority.js) —
  // para que abrir esta oportunidad se sienta continuación de esa elección,
  // no un registro suelto que hay que interpretar de cero.
  const { reason: priorityReason } = explainPriority(opp);

  return (
    <div className="relative h-full flex flex-col" data-testid={T.oppDetailPanel}>
      {showFirstActionMoment && (
        <div
          data-testid={T.oppFirstActionMoment}
          className="absolute inset-0 z-30 bg-[#0d1321]/98 backdrop-blur-sm flex flex-col items-center justify-center text-center px-8"
        >
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-5">
            <CheckCircle2 size={28} className="text-emerald-400" />
          </div>
          <div className="font-display font-black text-xl text-zinc-50 max-w-sm">
            Ya completaste tu primera acción con Commercial Operator.
          </div>
          <p className="text-sm text-zinc-400 mt-2 max-w-sm leading-relaxed">
            Así se ve cada día: revisas quién necesita atención, decides, y avanzas al siguiente.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-bold px-6 py-3 rounded-full bg-amber-500 text-black hover:bg-amber-400 btn-glow-amber"
          >
            Ver mi siguiente prioridad <ArrowUpRight size={16} />
          </button>
          <button
            onClick={() => setShowFirstActionMoment(false)}
            className="mt-3 text-xs text-zinc-500 hover:text-zinc-300"
          >
            Seguir aquí
          </button>
        </div>
      )}
      {/* Header */}
      <div className="px-4 sm:px-6 py-5 border-b border-white/[0.06]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display font-bold text-lg tracking-tight text-zinc-50 leading-tight break-words">
              {opp.companyName}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {opp.contactName} · {bundle.actor?.contactRole || "Contacto"}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <PriorityBadge priority={opp.priority} />
            <ResponseBadge status={opp.responseStatus} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-0 mt-5 border-t border-white/[0.06] -mx-4 px-4 sm:-mx-6 sm:px-6 pt-4">
          <div className="border-r border-white/[0.06] pr-2 sm:pr-3">
            <AuditableFigure estimate={opp.estimatedValue} size="sm" tone="amber" label="Valor" />
          </div>
          <div className="border-r border-white/[0.06] px-2 sm:px-3">
            <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-mono">
              Probabilidad de cierre
            </div>
            <div className="font-display font-black text-xl tabular-nums text-zinc-100 mt-1">
              {Math.round(opp.probability * 100)}%
            </div>
          </div>
          <div className="pl-2 sm:pl-3">
            <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-mono">
              {timeSectionLabel(opp.engineId)}
            </div>
            <div className="mt-1.5"><WindowGauge days={opp.windowDays} engineId={opp.engineId} compact /></div>
          </div>
        </div>

        <div className="mt-4 text-[11px] text-zinc-500 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono">
          <Zap size={11} className="text-amber-500" />
          Qué encontramos: <span className="text-zinc-300">{opp.lastSignalLabel}</span>
          <span className="text-zinc-700 sm:ml-auto">actualizada {fmtRelative(opp.updatedAt)}</span>
        </div>

        {priorityReason && (
          <div className="mt-3 text-xs text-zinc-400 leading-relaxed border-l-2 border-white/[0.08] pl-3">
            Por qué es tu prioridad ahora: {priorityReason}
          </div>
        )}
      </div>

      {nextAction && (
        <div
          data-testid={T.oppNextAction}
          className="px-4 sm:px-6 py-3 border-b border-white/[0.06] bg-amber-500/5 flex items-center gap-2.5"
        >
          <Zap size={12} className="text-amber-400 shrink-0" />
          <div className="text-xs text-amber-200">
            <span className="font-mono uppercase tracking-[0.15em] text-[9px] text-amber-500 mr-2">
              Siguiente acción
            </span>
            {nextAction}
          </div>
        </div>
      )}

      {/* Play */}
      {play && (
        <>
          <div
            data-testid={T.oppPlayCard}
            className="px-4 sm:px-6 py-4 border-b border-white/[0.06]"
          >
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                  Esto le podrías decir
                </div>
                <div className="text-sm text-zinc-200 leading-relaxed mt-1">
                  <span className="inline-flex items-center gap-1.5 mr-2 text-amber-400">
                    <ChannelIcon size={12} />
                    <span className="text-[10px] font-mono uppercase tracking-[0.15em]">
                      {play.channel}
                    </span>
                  </span>
                  {play.title}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] font-mono text-zinc-500 uppercase tracking-[0.15em]">
                  Qué tan efectivo es
                </div>
                <div className="text-emerald-400 font-mono font-bold text-sm tabular-nums">
                  +{Math.round(play.expectedLiftPct * 100)}% respuestas
                </div>
              </div>
            </div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 font-mono mt-3">
              Por qué
            </div>
            <div className="text-[10px] text-zinc-500 leading-relaxed border-l-2 border-white/[0.08] pl-3 mt-1">
              {play.rationale}
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 border-b border-white/[0.06]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                Mensaje listo para enviar
              </div>
              <button
                data-testid={T.oppPlayCopy}
                onClick={() => {
                  navigator.clipboard.writeText(suggested);
                  toast.success("Copiado al portapapeles");
                }}
                className="text-[10px] text-zinc-500 hover:text-amber-400 flex items-center gap-1 font-mono uppercase tracking-[0.15em]"
              >
                <Copy size={11} /> Copiar
              </button>
            </div>
            <textarea
              value={suggested}
              onChange={(e) => setSuggested(e.target.value)}
              rows={6}
              // text-base (16px) en vez de text-sm: por debajo de 16px, Safari
              // en iOS hace zoom automático al enfocar el campo — justo lo
              // que "sin zoom manual" pide evitar. Mismo tamaño en escritorio
              // que antes de facto (nunca fue una decisión visual, era el
              // default de Tailwind), así que no se ve distinto ahí.
              className="w-full rounded-xl bg-white/[0.03] border border-white/[0.07] text-base sm:text-sm text-zinc-200 p-3 leading-relaxed focus:outline-none focus:border-amber-500/60 transition-colors resize-none"
            />
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                data-testid={T.oppPlaySave}
                onClick={savePlay}
                disabled={saving}
                className="text-[11px] font-mono uppercase tracking-[0.15em] px-3 py-2 sm:py-1.5 rounded-full border border-white/[0.1] text-zinc-200 hover:border-amber-500/60 hover:text-amber-400 transition-colors disabled:opacity-50"
              >
                Guardar
              </button>
              <button
                data-testid={T.oppPlaySend}
                onClick={async () => {
                  try {
                    const res = await app.opportunity.sendPlay(opp.id, { channel: play.channel });
                    if (res?.ok) {
                      toast.success("Enviado. Te avisamos cuando responda.");
                      setBundle((prev) =>
                        prev.opportunity.responseStatus === "not_contacted"
                          ? { ...prev, opportunity: { ...prev.opportunity, responseStatus: "awaiting" } }
                          : prev
                      );
                      celebrateIfFirstAction();
                    } else if (res?.error === "channel_not_connected") {
                      // Distinto de "missing_contact_id": el contacto SÍ está
                      // vinculado y GHL SÍ aceptó la petición, pero confirmó
                      // que el canal (ej. WhatsApp) no tiene una suscripción
                      // activa en esta Location — un problema de configuración
                      // de GHL, no de vinculación ni de Commercial Operator.
                      toast.error(
                        "GoHighLevel no pudo entregar el mensaje: el canal de WhatsApp de esta cuenta no está activo. Contacta a soporte de HighLevel para conectarlo."
                      );
                    } else if (res?.error === "missing_contact_id") {
                      // Hallazgo real (diagnóstico WhatsApp E2E, agosto 2026):
                      // este caso se mostraba con el mismo texto genérico de
                      // "canal no conectado" de abajo, ocultando la causa real
                      // -- este cliente nunca se vinculó con un contacto de
                      // GHL (resolve_contact no encontró coincidencia al
                      // importarlo), por lo que el envío nunca llegó a
                      // intentarse contra la API de GHL.
                      toast.error(
                        "Este cliente todavía no está vinculado con GoHighLevel — no se pudo enviar. Verifica que su teléfono coincida con un contacto real en GHL."
                      );
                    } else {
                      toast.info("Todavía no tenemos este canal conectado — en cuanto lo conectemos, esto se envía solo.");
                    }
                  } catch (e) {
                    toast.info("Todavía no tenemos este canal conectado — en cuanto lo conectemos, esto se envía solo.");
                  }
                }}
                className="text-[11px] font-mono uppercase tracking-[0.15em] px-3 py-2 sm:py-1.5 rounded-full flex items-center gap-1.5 bg-amber-500 text-black hover:bg-amber-400 btn-glow-amber"
              >
                <Send size={11} /> Enviar
              </button>
            </div>
          </div>
        </>
      )}

      {/* Response status — manual mientras no exista un canal real con
          webhook de entrada. Honesto: solo refleja lo que el usuario ya
          sabe que pasó fuera del sistema (llamada, WhatsApp personal, etc.) */}
      <div className="px-4 sm:px-6 py-4 border-b border-white/[0.06]">
        <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-3">
          ¿Ya te respondió?
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: "replied", label: "Respondió" },
            { key: "interested", label: "Interesado" },
            { key: "not_interested", label: "No le interesó" },
          ].map((r) => (
            <button
              key={r.key}
              disabled={saving}
              onClick={() => setResponseStatus(r.key)}
              className={`text-[11px] px-3 py-2 sm:py-1.5 rounded-full border transition-colors ${
                opp.responseStatus === r.key
                  ? "border-blue-500/60 bg-blue-500/10 text-blue-300"
                  : "border-white/[0.08] text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Outcome */}
      <div className="px-4 sm:px-6 py-4 flex-1 flex flex-col">
        <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-3">
          Registrar resultado
        </div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {OUTCOME_BUTTONS.map((o) => {
            const Icon = o.icon;
            const active = opp.outcome === o.key;
            const cls = OUTCOME_TONES[o.color][active ? "active" : "idle"];
            return (
              <button
                key={o.key}
                data-testid={T.oppOutcomeBtn(o.key)}
                disabled={saving}
                onClick={() => setOutcome(o.key)}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-mono uppercase tracking-[0.1em] transition-colors ${cls}`}
              >
                <Icon size={13} />
                {o.label}
              </button>
            );
          })}
        </div>

        <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">
          Tus notas
        </div>
        <textarea
          data-testid={T.oppNotes}
          value={notes || ""}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Contexto, próximos pasos, insights..."
          className="w-full rounded-xl bg-white/[0.03] border border-white/[0.07] text-base sm:text-sm text-zinc-200 p-3 focus:outline-none focus:border-amber-500/60 transition-colors resize-none"
        />
      </div>
    </div>
  );
}
